/**
 * Departments View Page — read-only detail.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? '—'}</dd>
    </div>
  );
}

export function DepartmentsViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOne({ resource: 'departments', id: id! });
  const dept = data?.data as Record<string, unknown> | undefined;

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (!dept) return <div className="p-6 text-sm text-destructive">Department not found.</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/departments')}><ArrowLeft className="h-4 w-4" /></Button>
          <h1 className="text-2xl font-semibold">{String(dept.name ?? '')}</h1>
          <Badge variant={dept.is_active ? 'default' : 'secondary'}>{dept.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
        <Button variant="outline" onClick={() => navigate(`/departments/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />Edit
        </Button>
      </div>
      <Card>
        <CardHeader><CardTitle>Department Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Name" value={String(dept.name ?? '')} />
            <Field label="Code" value={String(dept.code ?? '')} />
          </dl>
          {dept.description && (
            <div className="mt-4 space-y-1">
              <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Description</dt>
              <dd className="text-sm">{String(dept.description)}</dd>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DepartmentsViewPage;
